<script lang="ts">
    import { navigate } from "svelte-navigator";
    import { Accordion, Alert, Button, Col, Container, Icon, Offcanvas, Row, Spinner } from "sveltestrap";
    import BackBtn from "../Components/BackBtn.svelte";
    import TopBar from "../Components/TopBar.svelte";
    import type { Form } from "../Model/Form";
    import type { FormInstance } from "../Model/FormInstance";
    import { answersFactory, answersService, eventsChannelFactory, formsService, instancesService, notificationsService, sessionService } from "../Services";
    import { Answer } from "../Model/Answer";
    import type { Question } from "../Model/Question";
    import { onDestroy, setContext } from "svelte";
    import type { EventsChannel } from "../Services/EventsChannel";
    import { readable, writable } from "svelte/store";
    import type { AppEvent } from "../global";
    import SectionAccordionItem from "../Components/Form/SectionAccordionItem.svelte";
    import type { FormMember } from "../Model/FormMember";
    import type { FormValidator } from "../Services/FormValidator";
    import { v4 as uuidv4 } from 'uuid';

    const _readonlyForm = writable<boolean>(false);
    const readonlyForm = readable<boolean>(false, set => _readonlyForm.subscribe(set));
    const _isFulfilled = writable<boolean>(false);
    const isFulfilled = readable<boolean>(false, set => _isFulfilled.subscribe(set));
    const instanceWritable = writable<FormInstance | undefined>(undefined);
    const instanceReadable = readable<FormInstance | undefined>(undefined, set => instanceWritable.subscribe(set));
    const questionsMap = new Map<string, Question>();
    const answersMap = new Map<string, Answer>();

    let instanceId: string = '';
    let instance: FormInstance;
    let member: FormMember;
    let form: Form;
    let isLoading = false;
    let isOwner = false;
    let loadComplete = false;
    let openEndOffcanvas = false;
    let isDeleting = false;
    let instanceChannel: EventsChannel;
    let formValidator: FormValidator;

    let blurInstace = false;
    let intervalInstance;
    let intervalGetInstance;

    setContext('getAnswer', getAnswer);
    setContext('readonlyForm', readonlyForm);
    setContext('isFulfilled', isFulfilled);
    setContext('saveAnswer', saveAnswer);
    setContext('instance', instanceReadable);
    setContext('updateValidationState', updateIsFulfilledState);

    window.addEventListener('online', async () => {
        blurInstace = true;
        changeOnline()
    });

    function changeOnline(){
        intervalGetInstance = setInterval(() => {
            getNewSaveInstance();
        }, 5000);
    }


    async function getNewSaveInstance() {
        clearInterval(intervalGetInstance);
        console.log(instance.id);
        
        const instanceNew = await instancesService.getInstance(instance.id);

        console.log({instanceNew});
        
        if (instanceNew) {
            const url = `/formulario/${instance.id}`;
            clearInterval(intervalInstance);
            navigate(url); // Utiliza la función navigate() del enrutador de Svelte
        }else{
            intervalInstance = setInterval(() => {
                changeOnline();
            }, 5000);
        }


    }


    $: paramsChange();

    onDestroy(() => {
        instanceChannel?.destroy();
        instance?.destroy();
        clearInterval(intervalInstance);
        clearInterval(intervalGetInstance);
    });

    function paramsChange() {
        getInstance();
    }

    async function getInstance(): Promise<void> {
        try {

            const instanceId = localStorage.getItem(`instanceOffline`);
            
            instanceChannel?.destroy();

            loadComplete = false;
            isLoading = true;
        
            instance = await instancesService.getInstance(instanceId);
            
            if (!instance) {
                return;
            }
            
            if (!instance.hasMember(sessionService.user.id)) {   
                //El usuario actual no es miembro de este formulario
                navigate('/inicio');
                return;
            }

            instancesService.createOffLineInstance(instance);
            
            $instanceWritable = instance;
            isOwner = instance.hasOwner(sessionService.user.id);
            member = instance.getMember(sessionService.user.id);
            $_readonlyForm = !isOwner;
            instanceChannel = eventsChannelFactory.createEventsChannel(instance.id);

            instanceChannel.on('answer', onAnswer);
            instanceChannel.on('removed', onRemoved);
            instanceChannel.on('member_remove', onMemberRemove);

            let answers: Answer[];
            
            [form, answers] = await Promise.all([
                formsService.getForm(instance.formId),
                answersService.getInstanceAnsers(instanceId),
            ]);

            answersMap.clear();
            questionsMap.clear();

            answers.forEach(answer => answersMap.set(answer.questionId, answer));
            form.getAllQuestions().forEach(question => questionsMap.set(question.id, question));


            for (const answer of answers) {
                answer.id = uuidv4();
                answer.instanceId = instance.id;
                saveAnswer(answer);
            }

            loadComplete = true;

        } catch (error) {
            console.error(error);
        } finally {
            isLoading = false;
        }
    }

    async function saveAnswer(answer: Answer): Promise<void> {
        await answersService.saveAnswer(answer);
        await updateIsFulfilledState();
    }

    async function onAnswer({ data }: AppEvent): Promise<void> {
        //Creamos la respuesta si no existe
        if (!answersMap.has(data.questionId)) {
            const answer = Answer.fromPrimitives(data);
            answersMap.set(answer.questionId, answer);
        }
        //Actualizamos la respuesta
        answersMap.get(data.questionId).value = data.value;
        await updateIsFulfilledState();
    }

    function onRemoved(): void {
        //La instancia ha sido eliminada
        if (isDeleting) {
            return;
        }
        notificationsService.success('Eliminado', 'El formulario ha sido eliminado.');
        navigate('/inicio');
    }

    function onMemberRemove({ data: userId }: AppEvent<string>): void {
        if (userId === sessionService.user.id) {
            //Se ha eliminado al usuario actual como miebro del formulario
            notificationsService.notify('Miembro retirado', 'Ha sido retirado como miembro del formulario.');
            navigate('/inicio');
        }
    }

    async function updateIsFulfilledState(): Promise<void> {
        $_isFulfilled = await formValidator.validate();
    }

    function getAnswer(questionId: string): Answer {
        if (!answersMap.has(questionId)) {
            const question = questionsMap.get(questionId);
            const answer = answersFactory.createAnswer(question, instance.id);
            answersMap.set(questionId, answer);
        }
        return answersMap.get(questionId);
    }

    function toggleEndOffcanvas() {
        openEndOffcanvas = !openEndOffcanvas;
    }

    async function onRemoveClick() {
        if (isDeleting || !confirm('Se eliminará el formulario y todas las respuestas proporcionadas. Esto no se puede deshacer.\n¿Está seguro de continuar?')) {
            return;
        }

        try {
            isDeleting = true;
            await instancesService.removeInstance(instanceId);
            notificationsService.success('Eliminado', 'El formulario ha sido eliminado correctamente.');
            navigate('/inicio');
        } catch (error) {
            console.error(error);
            notificationsService.error('Error', 'Ha ocurrido un error al eliminar el formulario.');
            isDeleting = false;
        }
    }

</script>

<style lang="scss">

    @use '../variables';

    .view {
        display: flex;
        flex-direction: column;
    }

    .content {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        max-height: calc(100% - 120px);
        overflow: auto;
        background-color: #fff;
    }

</style>

<div class="view">
    <TopBar title={form?.name || "Formulario"}>
        <BackBtn slot="left" href="/inicio" />
        <div slot="right">
            {#if isOwner}
            <Button color="light" slot="right" on:click={toggleEndOffcanvas}>
                <Icon name="three-dots-vertical" />
            </Button>
            {/if}
        </div>
    </TopBar>

    <div class="content">

        {#if isLoading || blurInstace}
        <div class="d-flex flex-d text-center justify-content-center my-4">
           
            {#if blurInstace}
                <Alert color="warning">
                    <h5 class="mb-3">
                        Sincronizando
                    </h5>
                    <Spinner color="secondary" />
                </Alert>
            {:else}
                <Spinner color="primary" />
            {/if}

        </div>    
        {/if}

        {#if loadComplete && !blurInstace}

        <Container class="bg-white shadow py-3 mb-4">
            <Row>
                <Col>
                    <h2 class="m-0">{form.name}</h2>
                    
                    <p>{form.description}</p>

                </Col>
            </Row>
        </Container>

        <Accordion flush>

            {#each form.sections as section, index}
                <SectionAccordionItem
                    instanceId={instance.id}
                    sectionInstance={instance.sections.get(section.id)}
                    {section}
                    number={index + 1} />
            {/each}

        </Accordion>

        <Alert color="warning">
            <h5 >
                Formulario sin sincronizar
            </h5>
        </Alert>

        {/if}
    </div>

</div>

{#if isOwner}
<Offcanvas isOpen={openEndOffcanvas} toggle={toggleEndOffcanvas} placement="end" header="Opciones" style="width: 300px">
    <Button color="danger" block on:click={onRemoveClick}><Icon name="x-octagon-fill" /> Eliminar</Button>
</Offcanvas>
{/if}

